import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUp, ArrowDown, Baby, HeartHandshake, Users } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";
import { AnimatePresence, motion } from "framer-motion";

export function StorySection({ home }) {
  return (
    <section className="bg-ivory py-24 lg:py-40 px-6 md:px-10 lg:px-14" data-testid="story-section">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <Reveal><div className="overline mb-8">{home.story_eyebrow}</div></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.0] text-espresso">
              {home.story_headline_1}<br />
              <span className="italic text-taupe">{home.story_headline_2}</span>
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-10 text-lg lg:text-xl font-light leading-relaxed text-espressosoft/80 max-w-lg">
              {home.story_body}
            </p>
          </Reveal>
          <Reveal delay={260}>
            <Link to="/approach" className="mt-10 inline-flex items-center gap-3 text-espresso text-xs uppercase tracking-[0.2em] group" data-testid="story-approach-link">
              Discover Treat the Chain
              <span className="w-10 h-px bg-espresso group-hover:w-16 transition-all duration-500" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={200} className="lg:col-span-6">
          <div className="overflow-hidden rounded-sm aspect-[4/5] lg:ml-auto lg:max-w-md">
            <img src={resolveImg(home.story_image)} alt="Movement and natural light" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function TreatTheChainSection({ home }) {
  return (
    <section className="bg-linen py-24 lg:py-32 px-6 md:px-10 lg:px-14" data-testid="chain-section">
      <div className="max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-8">
            <div className="overline mb-6">Treat the Chain</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-espresso">
              {home.chain_headline_1} <span className="italic text-taupe">{home.chain_headline_2}</span>
            </h2>
            <h3 className="font-serif font-light text-2xl sm:text-3xl lg:text-4xl mt-6 text-espressosoft/60">
              {home.chain_sub_1} {home.chain_sub_2}
            </h3>
            <p className="mt-8 text-lg lg:text-xl font-light leading-relaxed text-espressosoft/80 max-w-2xl">
              Here's the part most people miss: every link in that chain — fascia, muscle, ligament and joint — is wired to one control system. Your <span className="text-espresso">nervous system</span> reads the whole chain and decides how you move, brace and feel pain. So we don't just chase the sore spot — we change the input your nervous system receives, and the output changes with it.
            </p>
          </Reveal>
        </div>
        <Reveal className="mt-12">
          <Link to="/approach" className="inline-block">
            <span className="bg-espresso text-ivory hover:bg-espressosoft rounded-full px-7 py-3.5 text-xs uppercase tracking-[0.18em] transition-colors" data-testid="chain-explore-link">
              Explore the Body Map
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function GoalsSection({ home }) {
  const goals = home?.goals || [];
  const [goalIdx, setGoalIdx] = useState(0);
  useEffect(() => {
    if (!goals.length) return;
    const t = setInterval(() => setGoalIdx((i) => (i + 1) % goals.length), 3200);
    return () => clearInterval(t);
  }, [goals.length]);

  return (
    <section className="bg-espresso text-ivory py-20 lg:py-32 px-6 md:px-10 lg:px-14 overflow-hidden" data-testid="goals-section">
      <div className="max-w-[1500px] mx-auto text-center">
        <Reveal><div className="overline !text-brass mb-8">{home.goals_eyebrow}</div></Reveal>
        <Reveal delay={80}>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-ivory/90 mb-14">{home.goals_headline}</h2>
        </Reveal>
        <div className="relative h-40 lg:h-48 flex items-center justify-center" data-testid="goals-rotator">
          <AnimatePresence mode="wait">
            {goals.length > 0 && (
            <motion.p
              key={goals[goalIdx]}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6 }}
              className="font-serif italic text-3xl sm:text-4xl lg:text-6xl px-4 max-w-5xl"
              data-testid="goals-rotator-text"
            >
              "{goals[goalIdx]}"
            </motion.p>
            )}
          </AnimatePresence>
        </div>
        <Reveal delay={120}>
          <p className="mt-10 text-xl lg:text-2xl font-light text-ivory/60">Your goal becomes our goal.</p>
          <div className="mt-10">
            <BookButton label={home.goals_cta || "Start Your Visit"} variant="brass" track_label="goals" testid="goals-book-button" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function ServicesSection({ services }) {
  return (
    <section className="bg-ivory py-24 lg:py-40 px-6 md:px-10 lg:px-14" data-testid="services-section">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <Reveal>
            <div className="overline mb-5">Our Treatments</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-espresso">Considered, individualized care.</h2>
          </Reveal>
          <Reveal delay={100}>
            <Link to="/treatments" className="text-xs uppercase tracking-[0.2em] text-espresso flex items-center gap-2 link-underline" data-testid="services-view-all">
              View all treatments <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
          {services.slice(0, 6).map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 80}>
              <Link to={`/treatments/${s.slug}`} className="group block" data-testid={`service-card-${s.slug}`}>
                <div className="overflow-hidden rounded-sm aspect-[4/3] mb-5">
                  <img src={resolveImg(s.image)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out" loading="lazy" />
                </div>
                <div className="overline mb-2">{s.category}</div>
                <h3 className="font-serif text-2xl lg:text-3xl text-espresso group-hover:text-taupe transition-colors">{s.name}</h3>
                <p className="mt-2 text-espressosoft/70 font-light leading-relaxed">{s.tagline}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhatHurtsSection({ concerns }) {
  return (
    <section className="bg-stone py-24 lg:py-36 px-6 md:px-10 lg:px-14" data-testid="concerns-section">
      <div className="max-w-[1500px] mx-auto">
        <Reveal>
          <div className="overline mb-5">Not sure where to start?</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-espresso max-w-2xl">Where do you need relief?</h2>
          <p className="mt-6 text-lg font-light text-espressosoft/75 max-w-xl">Pain is often a nervous-system signal that something in the chain isn't moving or being controlled well. Start with what you're feeling — we'll connect it to the right assessment and care.</p>
        </Reveal>
        <div className="mt-12 flex flex-wrap gap-3">
          {concerns.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 6) * 40}>
              <Link
                to={`/conditions/${c.slug}`}
                data-testid={`concern-chip-${c.slug}`}
                className="inline-block bg-ivory hover:bg-espresso hover:text-ivory border border-espresso/10 rounded-full px-6 py-3 text-sm transition-all duration-300"
              >
                {c.name}
              </Link>
            </Reveal>
          ))}
        </div>
        <Link to="/treatments" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso link-underline" data-testid="concerns-treatments-link">
          Or browse our treatments <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

const AUDIENCE_CARDS = [
  { icon: HeartHandshake, image: "/api/media/file/07eb45ee-2bd7-4fb9-84ec-63f4d82d584c", alt: "Pregnancy chiropractic adjustment at Psalm 30:2", to: "/pregnancy", eyebrow: "Pregnancy & Postpartum", title: "Comfort as the body changes", body: "Gentle, pregnancy-safe care that supports the pelvis, low back and nervous system through every trimester and into recovery." },
  { icon: Baby, image: "/api/media/file/cc9564af-c8b7-4bc3-ac9a-bdc21bc0b6f3", alt: "Gentle newborn chiropractic care", to: "/pediatric", eyebrow: "Pediatrics", title: "Support from the very start", body: "Light, age-appropriate care that helps a developing nervous system and growing chain move, settle and thrive." },
  { icon: Users, image: "/api/media/file/6846066e-38ed-45c4-88f6-68a9035874c6", alt: "Family chiropractic care for every generation", to: "/family-care", eyebrow: "Whole Family", title: "One team for everyone", body: "From aches and injuries to staying well, we care for the whole household — always treating the chain, not just the sore spot." },
];

export function AudienceSection() {
  return (
    <section className="bg-linen py-24 lg:py-36 px-6 md:px-10 lg:px-14" data-testid="home-audience">
      <div className="max-w-[1500px] mx-auto">
        <Reveal>
          <div className="overline mb-5">Who we care for</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-espresso max-w-3xl leading-[1.05]">
            Built around growing families — <span className="italic text-taupe">from the womb onward.</span>
          </h2>
          <p className="mt-6 text-lg font-light text-espressosoft/75 max-w-2xl">
            Pregnancy, pediatrics and family care are the heart of our practice. Whatever brings you in — often pain, always a body that's connected — we care for the nervous system and the chain that runs through all of it.
          </p>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {AUDIENCE_CARDS.map((card, i) => (
            <Reveal key={card.to} delay={i * 90}>
              <Link to={card.to} data-testid={`audience-card-${i}`} className="group block h-full bg-ivory border border-espresso/10 rounded-sm overflow-hidden hover:-translate-y-1 transition-transform duration-500">
                <div className="overflow-hidden aspect-[4/3]">
                  <img src={resolveImg(card.image)} alt={card.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out" loading="lazy" data-testid={`audience-card-img-${i}`} />
                </div>
                <div className="p-8">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-espresso/5 mb-6"><card.icon size={22} className="text-brass" strokeWidth={1.5} /></div>
                <div className="overline mb-2">{card.eyebrow}</div>
                <h3 className="font-serif text-2xl lg:text-3xl text-espresso group-hover:text-taupe transition-colors">{card.title}</h3>
                <p className="mt-3 text-espressosoft/70 font-light leading-relaxed">{card.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-espresso">Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection({ testimonials }) {
  if (!testimonials.length) return null;
  return (
    <section className="bg-ivory py-24 lg:py-40 px-6 md:px-10 lg:px-14" data-testid="home-testimonials">
      <div className="max-w-[1500px] mx-auto">
        <Reveal><div className="overline mb-5">Patient Stories</div></Reveal>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="order-2 lg:order-1">
            <blockquote className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-espresso">
              "{testimonials[0].quote}"
            </blockquote>
            <div className="mt-8 text-sm uppercase tracking-[0.16em] text-taupe">
              {testimonials[0].name} · {testimonials[0].location}
            </div>
            <Link to="/patient-stories" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso link-underline" data-testid="stories-view-all">
              Read patient stories <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={120} className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-sm aspect-[4/5] lg:max-w-md lg:ml-auto">
              <img src={resolveImg(testimonials[0].image)} alt={testimonials[0].name} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function NervousTeaserSection() {
  return (
    <section className="bg-ivory py-24 lg:py-36 px-6 md:px-10 lg:px-14" data-testid="home-nervous-teaser">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <div className="overline mb-6">Nervous-System Based Chiropractic</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.03] text-espresso">
            Your nervous system<br /><span className="italic text-taupe">runs your body.</span>
          </h2>
          <p className="mt-8 text-lg lg:text-xl font-light leading-relaxed text-espressosoft/80 max-w-xl">
            Your brain and spinal cord direct every movement, every breath, how you sleep, heal and feel pain. When the body is under stress, the nervous system shifts into protection — tension, guarding, poor sleep and pain. Our care is designed to calm and clarify that system, so your body can move, rest and recover the way it was made to.
          </p>
          <p className="mt-5 text-lg font-light leading-relaxed text-espressosoft/80 max-w-xl">
            Every modality we use — chiropractic, myofascial and functional care — works by changing the information your nervous system receives. It's the thread behind everything we do, for growing bumps, little ones and the whole family.
          </p>
          <Link to="/nervous-system" className="mt-9 inline-flex items-center gap-3 text-espresso text-xs uppercase tracking-[0.2em] group" data-testid="home-nervous-link">
            How we care for the nervous system
            <span className="w-10 h-px bg-espresso group-hover:w-16 transition-all duration-500" />
          </Link>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-linen border border-espresso/10 rounded-sm p-7 text-center">
              <ArrowUp className="mx-auto text-brass mb-3" strokeWidth={1.4} />
              <div className="font-serif text-2xl text-espresso">Bottom-up</div>
              <p className="text-xs text-taupe mt-2">The body informs the brain</p>
            </div>
            <div className="bg-espresso text-ivory rounded-sm p-7 text-center">
              <ArrowDown className="mx-auto text-brass mb-3" strokeWidth={1.4} />
              <div className="font-serif text-2xl">Top-down</div>
              <p className="text-xs text-ivory/50 mt-2">The brain controls the body</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function ClosingCTASection({ home }) {
  return (
    <section className="relative py-32 lg:py-48 px-6 overflow-hidden" data-testid="home-closing-cta">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${resolveImg(home.family_image)})` }} />
      <div className="absolute inset-0 bg-espresso/70" />
      <div className="relative max-w-3xl mx-auto text-center text-ivory">
        <Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">Personalized care. Functional thinking.</h2>
          <p className="mt-6 text-lg font-light text-ivory/75">Get back to what matters — for you and your whole family.</p>
          <div className="mt-10">
            <BookButton label="Book Your First Visit" variant="dark" track_label="home-closing" testid="closing-book-button" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
